/**
 * Kundlistan under Nyhetsbrev: en rad per e-postadress som handlat.
 *
 * Reklam: kundens SENASTE köp avgör (senaste beskedet gäller). Den som
 * avregistrerat sig från nyhetsbrevet, eller tackat nej till erbjudandemejlet
 * i kassan, får aldrig reklam. Beskedet gäller e-postadressen, så det gäller
 * även senare köp. Samma regel används när nyhetsbrevet skickas till "Tidigare
 * kunder", så att listan och utskicket alltid stämmer.
 */

export interface CustomerOrderRow {
    email: string;
    name: string;
    marketingConsent: boolean;
    createdAt: number;
    sellerName?: string;
}

export interface CustomerListRow {
    email: string;
    name: string;
    /** Får reklam: senaste köpet sa ja och adressen har inte tackat nej. */
    marketing: boolean;
    /** Avregistrerad från nyhetsbrevet. */
    unsubscribed: boolean;
    /** Tackade nej till erbjudandemejlet i kassan. */
    declined: boolean;
    orders: number;
    lastOrderAt: number;
    sellers: string[];
}

const normalizeEmail = (e: string) => e.trim().toLowerCase();

/** Adresser som inte får reklam. source 'checkout_declined' = nej i kassan, annars avregistrering. */
export type Suppression = string | { email: string; source?: string | null };

export const buildCustomerList = (orders: CustomerOrderRow[], suppressed: Suppression[]): CustomerListRow[] => {
    const blocked = new Map<string, boolean>();
    for (const s of suppressed) {
        const email = normalizeEmail(typeof s === 'string' ? s : s.email);
        const declined = typeof s !== 'string' && s.source === 'checkout_declined';
        blocked.set(email, declined);
    }
    const byEmail = new Map<string, { latest: CustomerOrderRow; count: number; sellers: Set<string> }>();
    for (const o of orders) {
        const email = normalizeEmail(o.email || '');
        if (!email) continue;
        const entry = byEmail.get(email);
        if (!entry) {
            byEmail.set(email, { latest: o, count: 1, sellers: new Set(o.sellerName ? [o.sellerName] : []) });
            continue;
        }
        entry.count++;
        if (o.sellerName) entry.sellers.add(o.sellerName);
        if (o.createdAt > entry.latest.createdAt) entry.latest = o;
    }
    return [...byEmail.entries()]
        .map(([email, { latest, count, sellers }]) => {
            const declined = blocked.get(email) === true;
            const unsubscribed = blocked.has(email) && !declined;
            return {
                email,
                name: latest.name.trim(),
                marketing: latest.marketingConsent && !blocked.has(email),
                unsubscribed,
                declined,
                orders: count,
                lastOrderAt: latest.createdAt,
                sellers: [...sellers].sort((a, b) => a.localeCompare(b, 'sv')),
            };
        })
        .sort((a, b) => b.lastOrderAt - a.lastOrderAt);
};

// --- Fil för Excel ---

/**
 * En cell i en CSV-fil för svenska Excel. Text som börjar med = + - @
 * skulle Excel köra som en formel (kundens namn kommer från kassan), så den
 * får en apostrof först.
 */
const cell = (value: string | number): string => {
    let text = String(value ?? '');
    if (/^[=+\-@\t\r]/.test(text)) text = `'${text}`;
    return /[;"\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

const ymd = (ts: number) => new Date(ts).toLocaleDateString('sv-SE', { timeZone: 'Europe/Stockholm' });

/** Semikolon som avgränsare och BOM först, så att å, ä, ö och kolumnerna blir rätt i svenska Excel. */
export const customerListCsv = (rows: CustomerListRow[]): string => {
    const header = ['E-post', 'Namn', 'Reklam godkänd', 'Avregistrerad', 'Avböjt i kassan', 'Antal köp', 'Senaste köp', 'Säljare'];
    const lines = rows.map(r => [
        r.email, r.name, r.marketing ? 'Ja' : 'Nej', r.unsubscribed ? 'Ja' : 'Nej', r.declined ? 'Ja' : 'Nej', r.orders, ymd(r.lastOrderAt), r.sellers.join(', '),
    ].map(cell).join(';'));
    return '﻿' + [header.map(cell).join(';'), ...lines].join('\r\n') + '\r\n';
};

/** Adresserna som får reklam, en per rad — att klistra in i ett annat utskicksverktyg. */
export const marketingEmails = (rows: CustomerListRow[]): string =>
    rows.filter(r => r.marketing).map(r => r.email).join('\n');
